/**
 * CUSTOM asset for blocks/hl-article-sticky.liquid (2026-09-08).
 * UPSTREAM risk: LOW — net-new file. No Horizon internals imported, extended
 * or relied on; the element owns its own markup contract entirely.
 *
 * <hl-article-sticky> — the floating product prompt on journal articles.
 *
 * Modelled on <hl-sticky-cta> in assets/pdp-buybox.js, and different from it
 * in two ways that matter:
 *
 *   1. It has three inputs, not one. The bar shows when the reader is past the
 *      anchor (the block's own zero-height sentinel, so the trigger point is
 *      wherever the block sits in the template), hides again once the bottom
 *      product cards are on screen — two offers for the same product, one of
 *      them covering the other, is worse than either alone — and stays down
 *      entirely while the cookie consent modal is open.
 *
 *   2. It navigates. The bar is a plain link to the PDP, so there is no click
 *      handler here at all: no scroll interception, no focus management, no
 *      preventDefault. Browser navigation already does the accessible thing.
 *
 *   3. It measures the subtitle. The one piece of layout maths in the file,
 *      and it exists so the subtitle can scroll when it does not fit instead
 *      of being truncated. See #watchSubtitle.
 *
 * THE CONSENT CHECK IS NOT OPTIONAL ON THIS TEMPLATE. Hair Luxious runs
 * Shopify's built-in privacy banner, which on mobile renders as a CENTRED
 * MODAL over the article rather than a bottom bar, with no dismiss affordance
 * beyond its two buttons. A Clarity heatmap of the Swedish article on
 * 2026-09-07 recorded 974 of 1,307 taps (74.5%) landing on that modal. Putting
 * a second floating element on screen underneath it would add a competing tap
 * target to a screen that is already the page's biggest problem, so the bar
 * waits its turn.
 *
 * `#shopify-pc__banner` / `#shopify-pc__prefs` are Shopify platform ids, not
 * Horizon's, and this is the only place the file depends on anything outside
 * its own markup. If Shopify ever renames them the check silently stops
 * matching and the bar shows over the modal backdrop — visually untidy for one
 * decision, never broken — which is why it is a querySelector and not a hard
 * requirement.
 *
 * IntersectionObserver rather than a scroll listener, for the same reason as
 * the PDP bar: the only question is whether something is on screen, which is
 * exactly what the observer reports, at no cost on scroll frames. With no
 * IntersectionObserver at all the element does nothing and the bar never
 * appears — the CSS default is hidden, so a failure here costs the reader
 * nothing that was not already missing.
 */

const CONSENT_SELECTOR = '#shopify-pc__banner, #shopify-pc__prefs';

// Marquee reading speed, px per second, and the dwell built into the keyframes.
// 24px/s is roughly a comfortable read for 12px italic Marcellus; the keyframes
// spend 28% of the cycle held still at the two ends, hence the added seconds.
const MARQUEE_SPEED = 24;
const MARQUEE_DWELL = 5;
// Anything under this is a sub-pixel or font-hinting difference, not an
// overflow, and promoting it to a marquee would make the line twitch.
const MARQUEE_THRESHOLD = 4;

class HlArticleSticky extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-sticky-bar]');
    this.anchor = this.querySelector('[data-sticky-anchor]');
    if (!this.bar || !this.anchor || !('IntersectionObserver' in window)) return;

    this.pastAnchor = false;
    this.atProductCards = false;
    this.consentOpen = false;

    // The anchor sits in the article flow, so "scrolled past it" is simply its
    // rect having gone above the viewport top. Read from boundingClientRect
    // rather than isIntersecting because a zero-height element leaving the
    // viewport upwards and downwards are the same event otherwise. The
    // observer reports an initial state on observe(), so a page restored
    // mid-article is correct on the first frame.
    this.anchorObserver = new IntersectionObserver(
      ([entry]) => {
        this.pastAnchor = entry.boundingClientRect.top < 0;
        this.#render();
      },
      { threshold: 0 }
    );
    this.anchorObserver.observe(this.anchor);

    const endTarget = document.querySelector(this.dataset.hideWhen || '');
    if (endTarget) {
      this.endObserver = new IntersectionObserver(
        ([entry]) => {
          this.atProductCards = entry.isIntersecting;
          this.#render();
        },
        { threshold: 0 }
      );
      this.endObserver.observe(endTarget);
    }

    this.#watchConsent();
    this.#watchSubtitle();
    this.#render();
  }

  disconnectedCallback() {
    this.anchorObserver?.disconnect();
    this.endObserver?.disconnect();
    this.consentObserver?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.checkConsent) {
      document.removeEventListener('visitorConsentCollected', this.checkConsent);
    }
  }

  /**
   * The consent modal is injected asynchronously and removed on decision, and
   * neither moment is a scroll event, so the bar cannot learn about it from
   * the observers above. Two cheap signals cover both edges: Shopify's own
   * `visitorConsentCollected` for the decision, and a childList-only mutation
   * observer on <body> for the injection (the banner is appended as a direct
   * child of body, so no subtree walk is needed).
   */
  #watchConsent() {
    this.checkConsent = () => {
      const element = document.querySelector(CONSENT_SELECTOR);
      // getBoundingClientRect, not offsetParent: the banner is
      // position: fixed, and fixed elements report a null offsetParent whether
      // they are visible or not.
      const open = !!element && element.getBoundingClientRect().height > 0;
      if (open === this.consentOpen) return;
      this.consentOpen = open;
      this.#render();
    };

    this.checkConsent();
    this.consentObserver = new MutationObserver(this.checkConsent);
    this.consentObserver.observe(document.body, { childList: true });
    document.addEventListener('visitorConsentCollected', this.checkConsent);
  }

  /**
   * The subtitle is a full sentence in a pill that is at most 460px wide and
   * usually narrower, so on most products it does not fit: "Hair vitamins for
   * her. The complete formula." truncates to "…The complet…" on a 390px phone.
   * Rather than shortening the copy (it is the PDP's own subtitle metafield,
   * shared deliberately so the two cannot drift) the line scrolls.
   *
   * Measured, not assumed. The marquee is switched on only when the text
   * genuinely overflows its window, and the distance it travels is the real
   * overflow, so the same CSS serves a two-word subtitle and a long one.
   *
   * Re-measured on three signals: now, once webfonts have loaded (Marcellus
   * italic is synthesized from the roman face and its metrics change the line's
   * width when it swaps in), and on any resize of the bar, which on a phone is
   * mostly orientation changes and the in-app browser's chrome collapsing.
   */
  #watchSubtitle() {
    this.subtitle = this.querySelector('[data-sticky-subtitle]');
    if (!this.subtitle) return;

    this.measureSubtitle = () => {
      const window_ = this.subtitle.parentElement;
      if (!window_) return;
      const distance = this.subtitle.scrollWidth - window_.clientWidth;

      if (distance > MARQUEE_THRESHOLD) {
        const seconds = (distance / MARQUEE_SPEED) * 2 + MARQUEE_DWELL;
        this.style.setProperty('--hl-marquee-distance', `${Math.round(distance)}px`);
        this.style.setProperty('--hl-marquee-duration', `${seconds.toFixed(1)}s`);
        this.dataset.marquee = 'true';
      } else {
        this.dataset.marquee = 'false';
      }
    };

    this.measureSubtitle();
    document.fonts?.ready.then(this.measureSubtitle);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.measureSubtitle);
      this.resizeObserver.observe(this.bar);
    }
  }

  #render() {
    const visible = this.pastAnchor && !this.atProductCards && !this.consentOpen;
    this.dataset.visible = String(visible);
  }
}

if (!customElements.get('hl-article-sticky')) {
  customElements.define('hl-article-sticky', HlArticleSticky);
}
